import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      // Use withCredentials for cookie-based auth
      const response = await axios.get(
        `${API_URL}/api/payments/checkout/status/${sessionId}`,
        { withCredentials: true }
      );
      
      setPaymentDetails(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-900/20 to-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mb-4"></div>
            <p className="text-white text-lg">Confirming your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-gray-900 rounded-lg p-8 border border-green-500/20 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Payment Successful!
              </h1>
              <p className="text-gray-400">
                Thank you for subscribing to I Got Soul
              </p>
            </div>

            {paymentDetails && (
              <div className="bg-black/40 rounded-lg p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Amount Paid:</span>
                  <span className="text-white font-semibold">
                    ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Status:</span>
                  <span className="text-green-400 font-semibold">Confirmed</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/browse')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3"
              >
                Start Watching <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/account-settings')}
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Go to Account Settings
              </Button>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-gray-900 rounded-lg p-8 border border-yellow-500/20 shadow-xl text-center">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">⏳</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Payment Pending
            </h1>
            <p className="text-gray-400 mb-6">
              Your payment is being processed. This may take a few moments.
            </p>
            <Button
              onClick={checkPaymentStatus}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Check Status Again
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-gray-900 rounded-lg p-8 border border-red-500/20 shadow-xl text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Payment Status Unknown
            </h1>
            <p className="text-gray-400 mb-6">
              We couldn't verify your payment status. Please contact support if you were charged.
            </p>
            <div className="space-y-3">
              <Button
                onClick={checkPaymentStatus}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/browse')}
                variant="outline"
                className="w-full border-gray-600"
              >
                Go to Browse
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
